import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CreditCard, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reusable "Pay with Yoco" button.
 *
 * Props:
 *  - amountZar (number, required): amount in Rand (e.g. 950 for R950.00)
 *  - customerName (string, required)
 *  - customerEmail (string, required)
 *  - description (string, optional)
 *  - metadata (object, optional) — extra key/value pairs saved on the order
 *  - label (string, optional): button label (default "Pay with Yoco")
 *  - className (string, optional): additional Tailwind classes
 *  - onError (fn, optional): called if checkout creation fails
 *  - testId (string, optional): data-testid override
 *
 * The button POSTs to /api/yoco/create-checkout on the backend which safely
 * calls Yoco with the server-only secret and returns a hosted redirect URL.
 * The customer is then sent to Yoco's checkout page.
 */
export const YocoCheckoutButton = ({
  amountZar,
  customerName,
  customerEmail,
  description,
  metadata,
  label = 'Pay with Yoco',
  className = '',
  onError,
  testId = 'yoco-checkout-btn',
}) => {
  const [loading, setLoading] = useState(false);

  const pay = async () => {
    if (!amountZar || !customerName || !customerEmail) {
      toast.error('Missing payment details');
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/yoco/create-checkout`,
        {
          customer_name: customerName,
          customer_email: customerEmail,
          amount_zar: Number(amountZar),
          description,
          metadata,
        },
        { withCredentials: true }
      );
      if (data?.redirect_url) {
        window.location.assign(data.redirect_url);
      } else {
        throw new Error('No redirect URL returned');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Could not start Yoco checkout';
      toast.error(detail);
      onError?.(err);
      setLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={pay}
      disabled={loading}
      className={`inline-flex items-center gap-2 bg-ink text-paper caps px-6 py-3 text-[11px] hover:bg-black transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
      data-testid={testId}
    >
      {loading ? (
        <>
          <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />
          Redirecting…
        </>
      ) : (
        <>
          <CreditCard className="w-4 h-4" strokeWidth={2} />
          {label}
        </>
      )}
    </button>
  );
};

export default YocoCheckoutButton;
